import React, { useEffect } from 'react';
import { array, object, bool, string, func, node, shape } from 'prop-types';
import { connect } from 'react-redux';
import { compose } from 'redux';
import _ from 'lodash';
import classNames from 'classnames';
import { getContext } from '@fiverr-private/fiverr_context';
import { sessionStorage } from '@fiverr-private/futile';
import Collections from '../Collections';
import CollectHeartBtn from '../CollectHeartBtn';
import { COLLECT_SESSION_STORAGE_KEY } from '../../utils/constants';
import isEqualResource from '../../utils/isEqualResource';
import raiseItemCreateSuccess from '../../utils/raiseItemCreateSuccess';
import { getDefaultCollection, getDefaultCollectionName } from '../../utils/defaultCollection';
import { createCollectionItem, createCollection } from '../../actions';
import eventsReporter from '../../utils/events/reporter';
import { EventTypes } from '../../utils/events/types';
import { contextConnect } from '../Context';

import './index.scss';

const Collect = ({
    inspireVariant = false,
    collections = [],
    createCollectionItem = _.noop,
    createCollection = _.noop,
    resource,
    showMenu = true,
    menuPosition,
    allowMenuOverflow = false,
    onCollectionClickedCallback = _.noop,
    onUpdateCollections = _.noop,
    educationTooltip,
    className,
    tooltipClassName,
    heartButtonClassName,
    hamburgerButtonClassName,
    heartIcon,
    hamburgerIcon,
    popoverContainerClassName,
    triggerData,
    source
}) => {
    const { userId } = getContext();
    const isLoggedIn = !!userId;

    useEffect(() => {
        collectFromStorage();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const collectFromStorage = async() => {
        const storageResourceId = sessionStorage.get(COLLECT_SESSION_STORAGE_KEY);

        if (storageResourceId !== resource.id) {return;}

        sessionStorage.remove(COLLECT_SESSION_STORAGE_KEY);

        if (!isLoggedIn) {return;}

        try {
            if (collections.length) {
                const { id, name } = getDefaultCollection(collections);
                await createCollectionItem({ id, resource, onUpdateCollections });
                raiseItemCreateSuccess(name);
            } else {
                const collectionName = getDefaultCollectionName();
                await createCollection({ name: collectionName, resource, onUpdateCollections });
            }
            eventsReporter.report(EventTypes.collection.storage.success);
        } catch (error) {
            eventsReporter.report(EventTypes.collection.storage.failure);
        }
    };

    const isCollected = collections.some((collection) =>
        collection.items.some((item) => isEqualResource(resource, item))
    );

    const shouldShowMenu = isLoggedIn && hamburgerIcon !== null && (showMenu || inspireVariant);
    const shouldShowSingleButton = heartIcon !== null && (!inspireVariant || !isLoggedIn);

    return (
        <div className={classNames('collect-package', className)}>
            {shouldShowMenu && (
                <Collections inspireVariant={inspireVariant}
                    isCollected={isCollected}
                    menuPosition={menuPosition}
                    allowMenuOverflow={allowMenuOverflow}
                    onCollectionClickedCallback={onCollectionClickedCallback}
                    tooltipClassName={tooltipClassName}
                    heartButtonClassName={heartButtonClassName}
                    hamburgerButtonClassName={hamburgerButtonClassName}
                    hamburgerIcon={hamburgerIcon}
                    heartIcon={heartIcon}
                    popoverContainerClassName={popoverContainerClassName}/>
            )}
            {shouldShowSingleButton && (
                <CollectHeartBtn inspireVariant={inspireVariant}
                    isCollected={isCollected}
                    isLoggedIn={isLoggedIn}
                    onCollectionClickedCallback={onCollectionClickedCallback}
                    educationTooltip={educationTooltip}
                    tooltipClassName={tooltipClassName}
                    heartButtonClassName={heartButtonClassName}
                    heartIcon={heartIcon}
                    triggerData={triggerData}
                    source={source}/>
            )}
        </div>
    );
};

Collect.propTypes = {
    inspireVariant: bool,
    collections: array,
    resource: object,
    showMenu: bool,
    onCollectionClickedCallback: func,
    menuPosition: string,
    allowMenuOverflow: bool,
    onUpdateCollections: func,
    createCollectionItem: func,
    createCollection: func,
    educationTooltip: object,
    className: string,
    tooltipClassName: string,
    heartButtonClassName: string,
    hamburgerButtonClassName: string,
    heartIcon: node,
    hamburgerIcon: node,
    popoverContainerClassName: string,
    triggerData: shape({
        triggerId: string,
        triggerCopy: string,
        triggerPlacement: string,
        triggerComments: string
    }),
    source: string
};

const mapStateToProps = ({ collections }) => ({ collections });

export { Collect };
export default compose(
    connect(mapStateToProps, {
        createCollectionItem,
        createCollection
    }),
    contextConnect()
)(Collect);
